import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { GameCard } from './GameCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Target,
  Loader2,
  Filter,
  BarChart3,
  CheckCircle2,
  XCircle,
  Search,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Confidence ranges matching user requirements
const CONFIDENCE_RANGES = [
  { label: '80-100%', min: 80, max: 100, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { label: '70-79%', min: 70, max: 79, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { label: '60-69%', min: 60, max: 69, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { label: '50-59%', min: 50, max: 59, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { label: '40-49%', min: 40, max: 49, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { label: '31-39%', min: 31, max: 39, color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { label: '10-30%', min: 10, max: 30, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
];

const LEAGUES = [
  { value: 'nba', label: 'NBA' },
  { value: 'nhl', label: 'NHL' },
  { value: 'nfl', label: 'NFL' },
  { value: 'mlb', label: 'MLB' },
  { value: 'ncaab', label: 'NCAAB' },
  { value: 'epl', label: 'EPL' },
  { value: 'mls', label: 'MLS' },
  { value: 'cfl', label: 'CFL' },
  { value: 'argentina', label: 'Argentina' },
  { value: 'brazil', label: 'Brazil' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Games' },
  { value: 'scheduled', label: 'Upcoming' },
  { value: 'in_progress', label: 'Live' },
  { value: 'final', label: 'Completed' },
];

export function ConfidenceBrowser({ isAdmin = false }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedLeagues, setSelectedLeagues] = useState([]); // Changed to array for multi-select
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [games, setGames] = useState([]);
  const [distribution, setDistribution] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalGames, setTotalGames] = useState(0);
  const [hasError, setHasError] = useState(false);
  
  // Refs for game cards to enable scroll-to functionality
  const gameCardRefs = React.useRef({});
  
  // Check for scroll restoration from navigation state
  React.useEffect(() => {
    const historyState = window.history.state?.usr;
    const scrollToGameId = historyState?.scrollToGame;
    if (scrollToGameId && games.length > 0) {
      setTimeout(() => {
        const gameElement = gameCardRefs.current[scrollToGameId];
        if (gameElement) {
          gameElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a brief highlight effect
          gameElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
          setTimeout(() => {
            gameElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 2000);
        }
      }, 300);
      // Clear the state after scrolling
      window.history.replaceState({}, document.title);
    }
  }, [games]);

  // Format date in LOCAL timezone (not UTC) to match user's expected date
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Toggle league selection
  const toggleLeague = (leagueValue) => {
    setSelectedLeagues(prev => {
      if (prev.includes(leagueValue)) {
        return prev.filter(l => l !== leagueValue);
      } else {
        return [...prev, leagueValue];
      }
    });
  };

  // Select/deselect all leagues
  const toggleAllLeagues = () => {
    if (selectedLeagues.length === LEAGUES.length) {
      setSelectedLeagues([]);
    } else {
      setSelectedLeagues(LEAGUES.map(l => l.value));
    }
  };

  const fetchGames = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        date: formatDate(selectedDate),
        ...(selectedRange && { confidence_min: selectedRange.min, confidence_max: selectedRange.max }),
        ...(selectedLeagues.length > 0 && { leagues: selectedLeagues.join(',') }),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
      });

      const response = await fetch(`${API_URL}/api/games/confidence-browser?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setGames(data.games || []);
        setDistribution(data.confidence_distribution || {});
        setTotalGames(data.total_games || 0);
      } else {
        const errorText = await response.text();
        console.error('Confidence browser error:', response.status, errorText);
        toast.error(`Failed to load games: ${response.status}`);
        setHasError(true);
      }
    } catch (err) {
      console.error('Error fetching games:', err);
      toast.error('Failed to load games. Please check your connection.');
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedRange, selectedLeagues, selectedStatus]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Filter games by search query (client-side filtering)
  const filteredGames = useMemo(() => {
    if (!searchQuery.trim()) return games;
    
    const query = searchQuery.toLowerCase().trim();
    return games.filter(game => {
      const homeTeam = game.home_team?.name?.toLowerCase() || '';
      const awayTeam = game.away_team?.name?.toLowerCase() || '';
      const homeAbbr = game.home_team?.abbreviation?.toLowerCase() || '';
      const awayAbbr = game.away_team?.abbreviation?.toLowerCase() || '';
      
      return homeTeam.includes(query) || 
             awayTeam.includes(query) ||
             homeAbbr.includes(query) ||
             awayAbbr.includes(query);
    });
  }, [games, searchQuery]);

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'bg-green-500/20 text-green-400';
    if (confidence >= 70) return 'bg-emerald-500/20 text-emerald-400';
    if (confidence >= 60) return 'bg-blue-500/20 text-blue-400';
    if (confidence >= 50) return 'bg-yellow-500/20 text-yellow-400';
    if (confidence >= 40) return 'bg-orange-500/20 text-orange-400';
    if (confidence >= 31) return 'bg-red-500/20 text-red-400';
    return 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="space-y-6" data-testid="confidence-browser">
      {/* Header with Date Navigation */}
      <Card className="bg-card/50">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Confidence Browser
              </CardTitle>
              <CardDescription>
                Browse games by platform confidence score
              </CardDescription>
            </div>
            
            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => navigateDate(-1)}
                data-testid="prev-date-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-secondary/50 rounded-md min-w-[180px] justify-center">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{formatDisplayDate(selectedDate)}</span>
              </div>
              
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => navigateDate(1)}
                data-testid="next-date-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button 
                variant="secondary" 
                size="sm"
                onClick={goToToday}
                data-testid="today-btn"
              >
                Today
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
              data-testid="team-search-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filters:</span>
            </div>
            
            {/* Multi-Select League Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-44 justify-between"
                  data-testid="league-filter"
                >
                  <span className="truncate">
                    {selectedLeagues.length === 0 
                      ? 'All Leagues' 
                      : selectedLeagues.length === LEAGUES.length 
                        ? 'All Leagues' 
                        : `${selectedLeagues.length} League${selectedLeagues.length > 1 ? 's' : ''}`}
                  </span>
                  <Filter className="w-4 h-4 ml-2 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-2 py-1 border-b">
                    <span className="text-sm font-medium">Select Leagues</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={toggleAllLeagues}
                    >
                      {selectedLeagues.length === LEAGUES.length ? 'Clear All' : 'Select All'}
                    </Button>
                  </div>
                  {LEAGUES.map(league => (
                    <div 
                      key={league.value} 
                      className="flex items-center gap-2 px-2 py-1 hover:bg-secondary/50 rounded cursor-pointer"
                      onClick={() => toggleLeague(league.value)}
                    >
                      <Checkbox 
                        checked={selectedLeagues.includes(league.value)}
                        onCheckedChange={() => toggleLeague(league.value)}
                      />
                      <span className="text-sm">{league.label}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-36" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {(selectedRange || searchQuery || selectedLeagues.length > 0) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSelectedRange(null);
                  setSearchQuery('');
                  setSelectedLeagues([]);
                }}
                className="text-muted-foreground"
              >
                Clear All Filters
              </Button>
            )}
          </div>
          
          {/* Confidence Range Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Platform Confidence Score:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {CONFIDENCE_RANGES.map((range) => {
                const count = distribution[range.label.replace('%', '')] || 0;
                const isSelected = selectedRange?.label === range.label;
                
                return (
                  <Button
                    key={range.label}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    className={`${isSelected ? '' : range.color} transition-all`}
                    onClick={() => setSelectedRange(isSelected ? null : range)}
                    data-testid={`confidence-range-${range.min}-${range.max}`}
                  >
                    {range.label}
                    <Badge 
                      variant="secondary" 
                      className="ml-2 text-xs"
                    >
                      {count}
                    </Badge>
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Stats Summary */}
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">
              Total: <span className="font-bold text-foreground">{totalGames}</span> games
            </span>
            {searchQuery && (
              <span className="text-sm text-muted-foreground">
                Search: <span className="font-bold text-foreground">{filteredGames.length}</span> matches for "{searchQuery}"
              </span>
            )}
            {selectedRange && !searchQuery && (
              <span className="text-sm text-muted-foreground">
                Showing: <span className="font-bold text-foreground">{filteredGames.length}</span> games in {selectedRange.label} range
              </span>
            )}
            {selectedLeagues.length > 0 && selectedLeagues.length < LEAGUES.length && (
              <span className="text-sm text-muted-foreground">
                Leagues: {selectedLeagues.map(l => LEAGUES.find(lg => lg.value === l)?.label).join(', ')}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Games Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-64 rounded-lg" />
          ))}
        </div>
      ) : hasError ? (
        <Card className="bg-card/50">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">Failed to load games</h3>
            <p className="text-sm text-muted-foreground mb-4">
              There was an issue loading the games. Please try again.
            </p>
            <Button onClick={fetchGames} variant="outline" size="sm">
              <Loader2 className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filteredGames.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredGames.map((game) => (
            <div 
              key={game.game_id} 
              ref={el => gameCardRefs.current[game.game_id] = el}
              className="relative transition-all duration-300"
            >
              {/* Confidence Badge Overlay */}
              <div className="absolute top-2 right-2 z-10">
                <Badge className={`${getConfidenceColor(game.confidence_score)} font-mono`}>
                  {game.confidence_score}%
                </Badge>
              </div>
              
              {/* Prediction Result Badge for Final Games */}
              {game.status === 'final' && game.prediction_correct !== undefined && (
                <div className="absolute top-2 left-2 z-10">
                  {game.prediction_correct ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Correct
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <XCircle className="w-3 h-3 mr-1" />
                      Incorrect
                    </Badge>
                  )}
                </div>
              )}
              
              <GameCard game={game} className="h-full" />
            </div>
          ))}
        </div>
      ) : (
        <Card className="bg-card/50">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No games found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery 
                ? `No teams match "${searchQuery}"`
                : selectedRange 
                  ? `No games in the ${selectedRange.label} confidence range for this date`
                  : 'No games scheduled for this date'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ConfidenceBrowser;
